import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import {
    Users, Activity, MessageSquare, Clock, Gauge, BookOpen, ArrowRight, Shield,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/Skeleton";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { cn } from "@/lib/utils";

const fmtNum = (n) => (n == null ? "—" : Number(n).toLocaleString());
const fmtPct = (n) => (n == null ? "—" : `${Math.round(n)}%`);
const fmtHours = (n) => (n == null ? "—" : `${Number(n).toFixed(1)}h`);

const relTime = (iso) => {
    if (!iso) return "—";
    try {
        return formatDistanceToNow(new Date(iso), { addSuffix: true });
    } catch {
        return iso;
    }
};

const StatCard = ({ icon: Icon, label, value, sublabel, testid }) => (
    <Card className="border-border/80 shadow-sm" data-testid={testid}>
        <CardContent className="p-5 space-y-2.5">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Icon className="h-3.5 w-3.5 text-primary" />
                {label}
            </div>
            <div className="text-3xl font-semibold tabular-nums tracking-tight text-foreground" data-testid={`${testid}-value`}>
                {value}
            </div>
            {sublabel && (
                <p className="text-xs text-muted-foreground" data-testid={`${testid}-sub`}>{sublabel}</p>
            )}
        </CardContent>
    </Card>
);

const SignupSparkline = ({ values }) => {
    const W = 320;
    const H = 60;
    const padX = 4;
    const padY = 6;
    const innerW = W - padX * 2;
    const innerH = H - padY * 2;
    const max = Math.max(1, ...values);
    const xFor = (i) => padX + (i / Math.max(1, values.length - 1)) * innerW;
    const yFor = (v) => padY + (1 - v / max) * innerH;
    const path = values.map((v, i) => `${i === 0 ? "M" : "L"}${xFor(i).toFixed(1)} ${yFor(v).toFixed(1)}`).join(" ");
    const last = values[values.length - 1];
    return (
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} className="overflow-visible" data-testid="signup-sparkline">
            <line x1={padX} x2={W - padX} y1={H - padY} y2={H - padY} stroke="hsl(var(--border))" strokeWidth="1" />
            {values.length > 1 && (
                <>
                    <path d={`${path} L ${xFor(values.length - 1)} ${H - padY} L ${xFor(0)} ${H - padY} Z`} fill="hsl(var(--primary))" fillOpacity="0.1" />
                    <path d={path} fill="none" stroke="hsl(var(--primary))" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </>
            )}
            <circle cx={xFor(values.length - 1)} cy={yFor(last)} r="3" fill="hsl(var(--primary))" />
        </svg>
    );
};

export default function AdminOverviewPage() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    useDocumentTitle("Admin · Overview");

    useEffect(() => {
        let cancelled = false;
        api.get("/admin/overview")
            .then((r) => { if (!cancelled) setData(r.data); })
            .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Failed to load overview"); });
        return () => { cancelled = true; };
    }, []);

    const totalUsers = data?.total_users ?? 0;
    const activePct = totalUsers
        ? Math.round((data.active_users_30d / totalUsers) * 100)
        : 0;

    return (
        <Layout>
            <div className="space-y-8 animate-fade-in-up" data-testid="admin-overview-page">
                <header className="space-y-1.5">
                    <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
                        <Shield className="h-3.5 w-3.5" />
                        Admin
                    </p>
                    <div className="flex flex-wrap items-end justify-between gap-3">
                        <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                            Platform overview
                        </h1>
                        <nav className="flex gap-2 text-xs font-medium" data-testid="admin-subnav">
                            <Link to="/admin/users" className="rounded-full border border-border bg-secondary px-3 py-1.5 hover:bg-background transition-colors" data-testid="admin-subnav-users">
                                Users
                            </Link>
                            <Link to="/admin/payments" className="rounded-full border border-border bg-secondary px-3 py-1.5 hover:bg-background transition-colors" data-testid="admin-subnav-payments">
                                Payments
                            </Link>
                            <Link to="/admin/support" className="rounded-full border border-border bg-secondary px-3 py-1.5 hover:bg-background transition-colors" data-testid="admin-subnav-support">
                                Support
                                {data?.support_tickets_awaiting_admin > 0 && (
                                    <span
                                        className="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold text-white tabular-nums"
                                        data-testid="admin-subnav-support-badge"
                                    >
                                        {data.support_tickets_awaiting_admin}
                                    </span>
                                )}
                            </Link>
                            <Link to="/admin/allowlist" className="rounded-full border border-border bg-secondary px-3 py-1.5 hover:bg-background transition-colors" data-testid="admin-subnav-allowlist">
                                Allowlist
                                {data?.complimentary_pending_allowlist > 0 && (
                                    <span
                                        className="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary/15 px-1 text-[10px] font-semibold text-primary tabular-nums"
                                        data-testid="admin-subnav-allowlist-badge"
                                    >
                                        {data.complimentary_pending_allowlist}
                                    </span>
                                )}
                            </Link>
                        </nav>
                    </div>
                    <p className="text-sm text-muted-foreground" data-testid="admin-overview-signed-in-as">
                        Signed in as <span className="font-medium text-foreground">{user?.email}</span>
                    </p>
                </header>

                {error && (
                    <Card className="border-destructive/50 bg-destructive/5">
                        <CardContent className="p-4 text-sm text-destructive">{error}</CardContent>
                    </Card>
                )}

                {!data ? (
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-overview-skeleton">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <Skeleton key={i} className="h-32 w-full rounded-2xl" />
                        ))}
                    </div>
                ) : (
                    <>
                        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-overview-stats">
                            <StatCard
                                icon={Users}
                                label="Total users"
                                value={fmtNum(data.total_users)}
                                sublabel={`+${data.signups_last_7d} this week · ${data.total_admins} admin`}
                                testid="stat-total-users"
                            />
                            <StatCard
                                icon={Activity}
                                label="Active users (30d)"
                                value={fmtNum(data.active_users_30d)}
                                sublabel={`${activePct}% of total`}
                                testid="stat-active-users"
                            />
                            <StatCard
                                icon={MessageSquare}
                                label="Total sessions"
                                value={fmtNum(data.total_sessions)}
                                sublabel={`${data.total_completed_sessions} completed · ${data.total_reviewed_sessions} reviewed`}
                                testid="stat-total-sessions"
                            />
                            <StatCard
                                icon={Clock}
                                label="Hours practiced"
                                value={fmtHours(data.total_hours_practiced)}
                                sublabel="Platform-wide"
                                testid="stat-hours"
                            />
                            <StatCard
                                icon={Gauge}
                                label="Avg session score"
                                value={fmtPct(data.average_session_score)}
                                sublabel={data.total_dynamic_cases_generated
                                    ? `${data.total_dynamic_cases_generated} dynamic cases generated`
                                    : "Across reviewed sessions"}
                                testid="stat-avg-score"
                            />
                            <StatCard
                                icon={BookOpen}
                                label="Learn attempts"
                                value={fmtNum(data.total_learn_attempts)}
                                sublabel={`Avg quiz score ${fmtPct(data.average_quiz_score)}`}
                                testid="stat-learn"
                            />
                        </section>

                        <section className="grid gap-6 lg:grid-cols-3">
                            {/* Recent activity */}
                            <Card className="border-border/80 shadow-sm lg:col-span-2" data-testid="admin-overview-recent">
                                <CardContent className="p-5">
                                    <div className="flex items-baseline justify-between mb-4">
                                        <h2 className="text-base font-semibold text-foreground">Recent activity</h2>
                                        <span className="text-xs text-muted-foreground">Latest reviewed sessions</span>
                                    </div>
                                    {data.recent_reviewed_sessions.length === 0 ? (
                                        <p className="py-8 text-center text-sm text-muted-foreground">
                                            No reviewed sessions yet across the platform.
                                        </p>
                                    ) : (
                                        <ul className="divide-y divide-border/70">
                                            {data.recent_reviewed_sessions.map((s) => (
                                                <li key={s.session_id}>
                                                    <Link
                                                        to={`/admin/users/${s.user_id}`}
                                                        data-testid={`admin-recent-row-${s.session_id}`}
                                                        className="flex items-center justify-between gap-3 py-3 group"
                                                    >
                                                        <div className="min-w-0 flex-1">
                                                            <p className="truncate text-sm font-medium text-foreground">
                                                                {s.user_email}
                                                            </p>
                                                            <p className="truncate text-xs text-muted-foreground">
                                                                {s.avatar_name} · {s.case_title}
                                                                <span className="mx-1.5 text-border">·</span>
                                                                {s.therapy_type_name}
                                                            </p>
                                                        </div>
                                                        <div className="flex shrink-0 items-center gap-3">
                                                            <span
                                                                className={cn(
                                                                    "rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums",
                                                                    s.overall_score >= 70
                                                                        ? "bg-emerald-100 text-emerald-700"
                                                                        : s.overall_score >= 50
                                                                            ? "bg-amber-100 text-amber-700"
                                                                            : "bg-rose-100 text-rose-700"
                                                                )}
                                                            >
                                                                {s.overall_score}
                                                            </span>
                                                            <span className="hidden sm:block text-xs text-muted-foreground tabular-nums w-24 text-right">
                                                                {relTime(s.ended_at)}
                                                            </span>
                                                            <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1" />
                                                        </div>
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Signups sparkline */}
                            <Card className="border-border/80 shadow-sm" data-testid="admin-overview-signups">
                                <CardContent className="p-5 space-y-3">
                                    <div className="space-y-1">
                                        <h2 className="text-base font-semibold text-foreground">Signups (30d)</h2>
                                        <p className="text-xs text-muted-foreground">
                                            <span className="text-xl font-semibold tabular-nums text-foreground" data-testid="admin-signups-30d">
                                                {data.signups_last_30d}
                                            </span>{" "}
                                            new users in the last 30 days
                                        </p>
                                    </div>
                                    <SignupSparkline values={data.signups_per_day_30d} />
                                </CardContent>
                            </Card>
                        </section>
                    </>
                )}
            </div>
        </Layout>
    );
}
